import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../../modules/BulkActions";
import { QUERY_PAYABLE, QUERY_PAYABLES } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { PayablesTableItemData } from "./PayablesTable";

const CANCELLABLE_STATUSES = new Set([
  InvoiceStatus.Open,
  InvoiceStatus.Overdue,
  InvoiceStatus.Pending
]);

function processCancelPayables(
  data: PayablesTableItemData[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> {
  const bulkAction = bulkActionFactory<PayablesTableItemData>({
    onProgress,
    action: async item => {
      if (!CANCELLABLE_STATUSES.has(item.payable?.status as InvoiceStatus)) {
        throw new Error(
          `Cannot cancel payable with status "${item.payable?.status}". Only open, overdue, and pending payables can be cancelled.`
        );
      }

      return await paymentsService.payable.update(
        item.payable?.payableId || "",
        { status: InvoiceStatus.Cancelled }
      );
    },
    onFinish: ({ successItems }) => {
      WSQueryCache.invalidateQueries(QUERY_PAYABLES);
      successItems.forEach(item => {
        WSQueryCache.invalidateQueries([
          QUERY_PAYABLE,
          item.payable?.payableId
        ]);
      });
      onSuccess?.();
    }
  });

  return bulkAction.run(data).then(({ successItems, failedItems }) => ({
    total: data.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
}

export function useBulkCancelPayables() {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayablesTableItemData[], onSuccess?: () => void) => {
    const count = items.length;
    const pluralized = count > 1 ? "payables" : "payable";

    return runBulkAction({
      actionName: "Cancel payables",
      itemCount: count,
      confirmDescription: `Are you sure you want to cancel ${count.toLocaleString()} ${pluralized}? Cancelled payables will not be paid.`,
      confirmLabel: "Cancel payables",
      confirmDestructive: true,
      prepare: async () => items,
      process: async (data, onProgress) =>
        processCancelPayables(data, onProgress, onSuccess)
    });
  };
}
